import { SHButton, SHStack } from "@components/design-systems";
import { FilterSVG } from "@components/svgs";
import { ModelScreenerFilterDTO } from "@models/model-screener/entities/modelScreenerFilter";
import { Badge, useTheme } from "@mui/material";
import { MSTableFilterModal } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-modal";
import { RootState } from "@redux/store";
import { generateUUID } from "@utils";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useToggle } from "react-use";

interface MSTableFilterProps {
  currentFilterData: ModelScreenerFilterDTO;
  disabled?: boolean;
}

export const MSTableFilter = ({ currentFilterData, disabled }: MSTableFilterProps) => {
  const { palette } = useTheme();

  const appliedFilter = useSelector(
    (state: RootState) => state.modelScreener.internal.appliedFilter,
  );
  const numberOfFiltersApplied = useSelector(
    (state: RootState) => state.modelScreener.ui.numberOfFiltersApplied,
  );
  const currentSelectedDateRange = useSelector(
    (state: RootState) => state.modelScreener.currentSelectedDateRange,
  );

  // States
  const [modalOpening, toggleOpenModal] = useToggle(false);
  const [modalKey, setModalKey] = useState(generateUUID());

  // Form
  const formMethod = useForm<ModelScreenerFilterDTO>({
    mode: "onChange",
    defaultValues: currentFilterData,
  });

  useEffect(() => {
    formMethod.reset(currentFilterData);
  }, [formMethod, currentFilterData]);

  useEffect(() => {
    setModalKey(generateUUID());
  }, [currentSelectedDateRange]);

  // Functions
  const handleOnModalClose = () => {
    toggleOpenModal(false);
    appliedFilter && formMethod.reset(appliedFilter);
  };

  const handleOnFilterButtonClick = () => {
    toggleOpenModal(true);
  };

  return (
    <FormProvider {...formMethod}>
      <SHStack direction={"row"} alignItems={"center"} gap={2}>
        <SHButton
          color="primary"
          size="extraMedium"
          variant="contained"
          onClick={handleOnFilterButtonClick}
          startIcon={
            <FilterSVG
              fill={palette.common.white}
              stroke={palette.common.white}
            />
          }
          endIcon={
            numberOfFiltersApplied > 0 ? (
              <Badge
                badgeContent={numberOfFiltersApplied}
                max={numberOfFiltersApplied}
                sx={{
                  ".MuiBadge-badge": {
                    position: "static",
                    backgroundColor: palette.warning.main,
                    color: palette.error.contrastText,
                    transform: "none",
                    minHeight: "22px",
                    minWidth: "22px",
                    paddingTop: "2px",
                    borderRadius: "100px",
                  },
                }}
              />
            ) : null
          }
          disabled={disabled}
        >
          Filters
        </SHButton>
      </SHStack>
      {currentFilterData && (
        <MSTableFilterModal
          key={`filter-modal-${modalKey}`}
          open={modalOpening}
          data={currentFilterData}
          onClose={handleOnModalClose}
        />
      )}
    </FormProvider>
  );
};
